/**
 * NPC livery (sprint-assembly-1, AD-647) — make a rig-fallback NPC read as a PERSON OF
 * THE WORLD rather than a second copy of the player.
 *
 * Why: no NPC pack exists (resolve.ts), so every NPC renders on `player-rig-v1` — the
 * same body, clothes and skin as a fresh player. The owner's playtest could not tell a
 * questgiver from another player. The rig already carries SEPARATE materials for skin,
 * cloth, trousers and boots (`base-skin` / `base-cloth` / `base-trousers` / `base-boots`,
 * each a texture × `baseColorFactor`), so a livery is only a per-NPC tint of those four:
 * the NPC's faction colours its tunic and trousers, and a deterministic per-id pick
 * varies skin tone and build. Nothing is generated and no mesh is added.
 *
 * PRESENTATION ONLY, frontend-local (the creature-name-map / RESOURCE_NODE_ASSET
 * convention pattern): never a `shared/` constant, never on the wire, no economy, no
 * collision. A real NPC pack bound through `NpcDef.presentation` bypasses this entirely
 * (resolve.ts takes the presentation branch first).
 *
 * The colours keep clear of the Bible §3.7 reserved combat hues (hostile `#e8443a`,
 * heal `#39c46a`) — pinned by livery.test.ts, like the ground palettes.
 *
 * Node-safe: structural slices of `THREE.Object3D`/`THREE.Material`, no `three` import.
 * Materials are CLONED before tinting: a loader clone shares its template's materials,
 * so tinting in place would repaint the player and every other NPC too.
 */

import type { BodyType } from '@great-cloud/shared/client';

/** The four tintable surfaces of `player-rig-v1`, by glTF material name. */
export const LIVERY_MATERIALS = {
  skin: 'base-skin',
  cloth: 'base-cloth',
  trousers: 'base-trousers',
  boots: 'base-boots',
} as const;

/** One faction's clothing colours (sRGB hex; applied as the material colour factor). */
export interface FactionLivery {
  readonly cloth: string;
  readonly trousers: string;
  readonly boots: string;
}

/**
 * Faction → clothing (the five canon factions, CLAUDE.md "Factions"). Chosen so each
 * reads at gameplay distance against the ground palettes: ChatNPC teal (guidance), the
 * Order of Clawed deep crimson (discipline), the Twin Minds violet (parallel minds),
 * the Herd moss (the commons), the Oracle of Sparks amber (the spark).
 */
export const FACTION_LIVERY: Readonly<Record<string, FactionLivery>> = {
  chatnpc: { cloth: '#3f8fa8', trousers: '#2b3a4a', boots: '#3b2a1c' },
  'the-order-of-clawed': { cloth: '#8e2f36', trousers: '#2a2a2e', boots: '#2b1d14' },
  'the-twin-minds': { cloth: '#6d4c9a', trousers: '#2f2a3d', boots: '#2e2233' },
  'the-herd': { cloth: '#6b7d3e', trousers: '#4a3b2a', boots: '#3a2616' },
  'the-oracle-of-sparks': { cloth: '#c8962e', trousers: '#3b3530', boots: '#2e2016' },
};

/** An NPC with no (or an unknown) faction: a plain slate tunic — still not the player's beige. */
export const DEFAULT_LIVERY: FactionLivery = {
  cloth: '#7c8492',
  trousers: '#34363b',
  boots: '#2e2218',
};

/** Skin tones (sRGB), light → deep; one is picked per NPC id. */
export const LIVERY_SKIN_TONES = ['#eac4a6', '#c99e85', '#b07e5f', '#8a5c40', '#5f3d29'] as const;

/** Builds picked per NPC id (a no-op on a rig with no morph targets). */
export const LIVERY_BODY_TYPES: readonly BodyType[] = ['slight', 'standard', 'sturdy'];

/** A resolved livery: what to paint on each surface, and the build. */
export interface NpcLivery {
  readonly cloth: string;
  readonly trousers: string;
  readonly boots: string;
  readonly skin: string;
  readonly bodyType: BodyType;
}

/** FNV-1a (32-bit) over a string — deterministic, so a capture reproduces (no Math.random). */
function fnv1a(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/** The livery for one NPC: its faction's clothes, plus a skin tone and build picked by id. */
export function npcLivery(npcId: string, faction: string | undefined): NpcLivery {
  const clothes = (faction !== undefined ? FACTION_LIVERY[faction] : undefined) ?? DEFAULT_LIVERY;
  const hash = fnv1a(npcId);
  const skin = LIVERY_SKIN_TONES[hash % LIVERY_SKIN_TONES.length] as string;
  const bodyType = LIVERY_BODY_TYPES[(hash >>> 8) % LIVERY_BODY_TYPES.length] as BodyType;
  return { ...clothes, skin, bodyType };
}

/** The colour slice of a `THREE.Color` (sRGB string in, working colour space stored). */
export interface LiveryColor {
  set(value: string): unknown;
}

/** The slice of a `THREE.Material` a livery touches. */
export interface LiveryMaterial {
  name: string;
  color?: LiveryColor;
  clone(): LiveryMaterial;
}

/** The traverse slice of a `THREE.Object3D`. */
export interface LiveryRoot {
  traverse(callback: (node: unknown) => void): void;
}

function surfaceColour(livery: NpcLivery, materialName: string): string | null {
  switch (materialName) {
    case LIVERY_MATERIALS.skin:
      return livery.skin;
    case LIVERY_MATERIALS.cloth:
      return livery.cloth;
    case LIVERY_MATERIALS.trousers:
      return livery.trousers;
    case LIVERY_MATERIALS.boots:
      return livery.boots;
    default:
      return null;
  }
}

/**
 * Paint a livery onto a built rig: every mesh material named for a livery surface is
 * replaced by a tinted CLONE (one clone per source material per NPC, shared across that
 * NPC's primitives). Returns how many materials were tinted — 0 for a rig whose materials
 * carry other names, which is the graceful no-op (the NPC keeps the default look).
 */
export function applyNpcLivery(root: LiveryRoot, livery: NpcLivery): number {
  if (typeof root.traverse !== 'function') return 0; // a bare stand-in root: nothing to paint
  const cloned = new Map<LiveryMaterial, LiveryMaterial>();
  const tint = (material: LiveryMaterial): LiveryMaterial => {
    const hit = cloned.get(material);
    if (hit !== undefined) return hit;
    const colour = surfaceColour(livery, material.name);
    if (colour === null || material.color === undefined) return material;
    const copy = material.clone();
    copy.color?.set(colour);
    cloned.set(material, copy);
    return copy;
  };
  root.traverse((node) => {
    const mesh = node as { material?: LiveryMaterial | LiveryMaterial[] };
    if (mesh.material === undefined) return;
    mesh.material = Array.isArray(mesh.material) ? mesh.material.map(tint) : tint(mesh.material);
  });
  return cloned.size;
}
